import type { ReactNode } from 'react';
import appLogo from '@/assets/upstand_ai_logo.svg';
import { Network, Sparkles } from 'lucide-react';
import { cn } from '@/components/ui/utils';

/** Discrete step bars (warm bronze + stone) - matches Stitch / M3-style onboarding reference. */
export function WorkspaceFlowSegmentedStepper({
  current,
  total,
  className,
}: {
  current: number;
  total: number;
  className?: string;
}) {
  return (
    <div
      className={cn('flex w-full gap-2', className)}
      role="progressbar"
      aria-valuenow={current}
      aria-valuemin={1}
      aria-valuemax={total}
    >
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={cn(
            'h-1 min-w-0 flex-1 rounded-full transition-colors duration-300',
            i < current ? 'bg-[#785128]' : 'bg-stone-200',
          )}
        />
      ))}
    </div>
  );
}

type WorkspaceFlowShellProps = {
  children: ReactNode;
  onSignOut: () => void;
  /** Optional FAB tooltip (onboarding copy by default). */
  fabTooltip?: string;
};

export function WorkspaceFlowShell({
  children,
  onSignOut,
  fabTooltip = "Hi, I'm your workspace guide. I'll help you finish setting up in less than 2 minutes.",
}: WorkspaceFlowShellProps) {
  return (
    <div className="flex min-h-screen flex-col bg-[#fbf9f5] font-sans text-foreground selection:bg-primary-container/30 selection:text-on-primary-container">
      <header className="sticky top-0 z-50 flex w-full items-center justify-between gap-4 px-6 py-5 sm:px-8 sm:py-6 bg-stone-50/80 backdrop-blur-xl">
        <div className="min-w-0 flex-1">
          <img
            src={appLogo}
            alt="Upstand.ai"
            width={370}
            height={98}
            className="h-12 w-auto max-w-[min(100%,280px)] object-contain object-left sm:h-[52px] sm:max-w-[300px] md:h-14 md:max-w-[340px]"
          />
        </div>
        <div className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground sm:gap-4">
          <button
            type="button"
            onClick={onSignOut}
            className="hidden text-xs font-medium text-muted-foreground hover:text-foreground sm:inline"
          >
            Sign out
          </button>
        </div>
      </header>

      {children}

      <footer className="mt-auto border-t border-border/30 bg-surface-container-low/80 px-6 py-10 sm:px-8">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-6 md:flex-row">
          <div className="flex items-center gap-2">
            <div className="onboarding-primary-gradient flex h-8 w-8 items-center justify-center rounded-full">
              <Network className="h-3.5 w-3.5 text-white" strokeWidth={2} />
            </div>
            <span className="font-display font-semibold tracking-tight text-headline">Upstand.ai</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <button type="button" className="hover:text-primary sm:hidden" onClick={onSignOut}>
              Sign out
            </button>
            <a href="#" className="transition-colors hover:text-primary">
              Privacy Policy
            </a>
            <a href="#" className="transition-colors hover:text-primary">
              Workspace terms
            </a>
            <a href="#" className="transition-colors hover:text-primary">
              Support Portal
            </a>
          </div>
        </div>
      </footer>

      <div className="group fixed bottom-10 right-10 z-50 flex h-14 w-14 cursor-help items-center justify-center rounded-full onboarding-primary-gradient shadow-2xl shadow-amber-600/35 backdrop-blur-md">
        <Sparkles className="h-7 w-7 text-white transition-transform group-hover:scale-110" />
        <div className="pointer-events-none absolute bottom-full right-0 mb-4 w-48 rounded-lg border border-white/20 bg-white/70 p-4 text-left text-xs leading-relaxed text-foreground opacity-0 shadow-xl backdrop-blur-md transition-opacity group-hover:opacity-100">
          {fabTooltip}
        </div>
      </div>
    </div>
  );
}

/** Shared background orbs + glow used under main content in workspace flows. */
export function WorkspaceFlowMainBackdrop() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 -z-10 onboarding-ethereal-glow" aria-hidden />
      <div
        className="pointer-events-none absolute top-20 right-10 -z-10 h-96 w-96 rounded-full bg-tertiary-container/10 blur-[100px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-20 left-10 -z-10 h-80 w-80 rounded-full bg-primary-container/10 blur-[80px]"
        aria-hidden
      />
    </>
  );
}
